import {
  Box,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useTheme,
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { format } from 'date-fns'
import React, { useContext, useState } from 'react'
import { NavLink } from 'react-router'
import * as ROUTES from 'routes/routes'
import { Timeline } from 'utils/__generated__/graphql'

import Clone from '../Clone'
import Create from '../Create'
import Delete from '../Delete'
import Edit from '../Edit'
import Publish from '../Publish'
import TimelinesContext, { TimelinesContextType } from '../context'

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) return -1
  if (b[orderBy] > a[orderBy]) return 1
  return 0
}

type Order = 'asc' | 'desc'

function getComparator(order: Order, orderBy: string) {
  return (a: Record<string, unknown>, b: Record<string, unknown>) => {
    const result = descendingComparator(a, b, orderBy as keyof typeof a)
    return order === 'desc' ? result : -result
  }
}

interface HeadCell {
  id: string
  label: string
  sort: boolean
}

const headCells: readonly HeadCell[] = [
  { id: 'name', label: 'Name', sort: true },
  { id: 'site', label: 'Site', sort: true },
  { id: 'company', label: 'Company', sort: true },
  { id: 'user', label: 'User', sort: true },
  { id: 'updatedAt', label: 'Updated At', sort: true },
  { id: 'createdAt', label: 'Created At', sort: true },
  { id: 'actions', label: '', sort: false },
]

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void
  order: Order
  orderBy: string
}

function EnhancedTableHead({
  order,
  orderBy,
  onRequestSort,
}: EnhancedTableProps) {
  const createSortHandler =
    (property: string) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property)
    }

  const theme = useTheme()

  return (
    <TableHead>
      <TableRow>
        {headCells.map(headCell => (
          <TableCell
            sx={{ color: theme.palette.text.primary, fontWeight: 'bold' }}
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.sort && (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

type TableRowType = {
  id: string
  name: string
  company: string
  site: string
  user: string
  createdAt: string
  updatedAt: string
  actions: React.ReactNode
}

function TimelineRow({ row }: { row: TableRowType }) {
  return (
    <TableRow>
      <TableCell>
        <NavLink to={`${ROUTES.TIMELINES}/${row.id}`}>
          {() => <span>{row.name}</span>}
        </NavLink>
      </TableCell>
      <TableCell>{row.site}</TableCell>
      <TableCell>{row.company}</TableCell>
      <TableCell>{row.user}</TableCell>
      <TableCell>
        {format(new Date(row.createdAt), 'MMM d, yyyy h:mm a')}
      </TableCell>
      <TableCell>
        {format(new Date(row.updatedAt), 'MMM d, yyyy h:mm a')}
      </TableCell>
      <TableCell>{row.actions}</TableCell>
    </TableRow>
  )
}

function TimelineActions({ timeline }: { timeline: Timeline }) {
  return (
    <Stack direction="row">
      <Publish timeline={timeline} />
      <Divider flexItem orientation="vertical" sx={{ m: 1 }} />
      <Edit timeline={timeline} />
      <Clone timeline={timeline} />
      <Delete timeline={timeline} />
    </Stack>
  )
}

function NoTimelinesFound() {
  const theme = useTheme()

  return (
    <Stack
      sx={{ justifyContent: 'center', alignItems: 'center', height: '100%' }}
    >
      <Typography
        sx={{ ...theme.typography.h4, color: theme.palette.text.secondary }}
      >
        No timelines found.
      </Typography>
      <Typography sx={{ color: theme.palette.text.secondary }}>
        <b>Click the + button to create one.</b>
      </Typography>
    </Stack>
  )
}

function TimelineTable() {
  const [order, setOrder] = useState<Order>('asc')
  const [orderBy, setOrderBy] = useState('company')

  const { timelines } = useContext<TimelinesContextType>(TimelinesContext)

  const handleRequestSort = (
    _: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const rows = timelines.map((timeline: Timeline) => ({
    id: timeline.id || '',
    name: timeline.name || '',
    company: timeline.company?.name || '',
    site: timeline.site?.name || '',
    user: `${timeline.user?.firstName || ''} ${timeline.user?.lastName || ''}`,
    createdAt: timeline.createdAt,
    updatedAt: timeline.updatedAt,
    actions: <TimelineActions timeline={timeline} />,
  }))

  const visibleRows = [...rows].sort(getComparator(order, orderBy))

  return (
    <Box sx={{ p: 1, position: 'relative', height: '100%' }}>
      <Create />
      {visibleRows.length ? (
        <TableContainer
          component={props => (
            <Paper {...props} elevation={0} variant="outlined" />
          )}
        >
          <Table aria-label="timelines table">
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {visibleRows.map((row: TableRowType) => (
                <TimelineRow key={row.id} row={row} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <NoTimelinesFound />
      )}
    </Box>
  )
}

export default TimelineTable
